import { useEffect, useState } from "react";
import {
  CircleNotch,
  CheckCircle,
  FloppyDisk,
  Eye,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";

export default function AdminLegal() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({ title: "", summary: "", content: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/legal-pages");
      setPages(r.data.pages || []);
      if (r.data.pages?.length && !active) {
        pickPage(r.data.pages[0]);
      }
    } catch {
      toast.error("Could not load legal pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickPage = (p) => {
    setActive(p);
    setDraft({
      title: p.title || "",
      summary: p.summary || "",
      content: p.content || "",
    });
  };

  const dirty =
    active &&
    (draft.title !== active.title ||
      draft.summary !== (active.summary || "") ||
      draft.content !== active.content);

  const save = async () => {
    if (!active) return;
    if (!draft.title.trim() || !draft.content.trim()) {
      toast.error("Title and content required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/admin/legal-pages/${active.slug}`, draft);
      toast.success(`${r.data.page.title} updated`);
      setPages((ps) => ps.map((p) => (p.slug === active.slug ? r.data.page : p)));
      setActive(r.data.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-legal-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
        Legal & Policies
      </p>
      <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
        Policy editor
      </h1>
      <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
        Edit the public Privacy Policy, Terms, AI Policy, and Refund Policy. Changes go live the moment you save and are recorded in the audit log.
      </p>

      {loading ? (
        <div className="mt-10 flex items-center gap-3 text-[#4A5568]">
          <CircleNotch className="animate-spin" weight="bold" /> Loading...
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[220px_1fr] gap-6">
          {/* Sidebar list */}
          <aside>
            <div className="mc-card p-2" data-testid="legal-list">
              {pages.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => pickPage(p)}
                  data-testid={`legal-pick-${p.slug}`}
                  className={`w-full text-left px-3 py-2.5 rounded-md text-sm smooth ${
                    active?.slug === p.slug
                      ? "bg-[#3B5BDB] text-white"
                      : "text-[#0A1128] hover:bg-[#F5EFE6]"
                  }`}
                >
                  {p.title}
                  {p.updated_at && (
                    <p
                      className={`text-[10px] mt-0.5 ${
                        active?.slug === p.slug ? "text-white/80" : "text-[#4A5568]"
                      }`}
                    >
                      Updated {new Date(p.updated_at).toLocaleDateString("en-IN")}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </aside>

          {/* Editor */}
          {active && (
            <div className="mc-card p-5 sm:p-6" data-testid="legal-editor">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
                <div>
                  <p className="text-xs text-[#4A5568]">
                    Public path:{" "}
                    <code className="bg-[#F5EFE6] px-1.5 py-0.5 rounded">/{active.slug}</code>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewOpen(true)}
                    data-testid="legal-preview-btn"
                    className="px-3 py-2 rounded-md border border-[#E8DFD1] text-sm hover:border-[#3B5BDB] smooth flex items-center gap-1.5"
                  >
                    <Eye size={14} /> Preview
                  </button>
                  <button
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="legal-save-btn"
                    className="px-4 py-2 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    {saving ? (
                      <CircleNotch size={14} className="animate-spin" weight="bold" />
                    ) : dirty ? (
                      <FloppyDisk size={14} weight="duotone" />
                    ) : (
                      <CheckCircle size={14} weight="duotone" />
                    )}
                    {saving ? "Saving..." : dirty ? "Save changes" : "Saved"}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                    Title
                  </label>
                  <input
                    data-testid="legal-title"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                    Summary (one-line description for search engines)
                  </label>
                  <input
                    data-testid="legal-summary"
                    value={draft.summary}
                    onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                    className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                    Content (Markdown — use ## for section headings to auto-build the contents list)
                  </label>
                  <textarea
                    data-testid="legal-content-area"
                    value={draft.content}
                    onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                    rows={26}
                    spellCheck="false"
                    className="mt-1 w-full px-3 py-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] font-mono text-sm leading-relaxed"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview modal */}
      {previewOpen && active && (
        <div
          className="fixed inset-0 z-50 bg-[#0A1128]/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && setPreviewOpen(false)}
          data-testid="legal-preview-modal"
        >
          <div className="bg-white rounded-2xl border border-[#E8DFD1] w-full max-w-3xl my-auto">
            <div className="px-6 py-4 border-b border-[#E8DFD1] flex items-center justify-between">
              <p className="font-display text-xl text-[#0A1128]">
                Preview: {draft.title}
              </p>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-[#4A5568] hover:text-[#0A1128]"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 max-h-[75vh] overflow-y-auto prose-mineai">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.content}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
